import { useState, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import api from './api';
import { CURRENCY_META, fmtMoney, curOf } from './ProjectsPage';

// ─── Mock data ────────────────────────────────────────────────────────────────

const COMPANY_TYPES = ['Client', 'Supplier', 'Both'];

const MOCK_PROJECTS = [];

export const initCompanies = () => [];

// ─── Styles ───────────────────────────────────────────────────────────────────

const thBase = {
  padding: '10px 14px', textAlign: 'left', color: 'var(--text-dim)',
  fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase',
  letterSpacing: '1px', borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap', background: 'var(--bg-card)', cursor: 'pointer',
  userSelect: 'none',
};
const tdStyle = {
  padding: '11px 14px', color: 'var(--text-body)', fontSize: '13px',
  borderBottom: '1px solid var(--bg-hover)', verticalAlign: 'middle', whiteSpace: 'nowrap',
};
const inputStyle = {
  background: 'var(--bg-input)', border: '1px solid var(--border-strong)', borderRadius: '6px',
  color: 'var(--text-body)', padding: '8px 12px', fontSize: '13px', width: '100%',
  boxSizing: 'border-box', outline: 'none',
};
const labelStyle = { color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' };

// ─── Type badge ───────────────────────────────────────────────────────────────

const typeConfig = {
  'Client':   { color: 'var(--info-text)', bg: 'var(--info-bg)' },
  'Supplier': { color: 'var(--warning-text)', bg: 'var(--warning-bg)' },
  'Both':     { color: 'var(--purple-text)', bg: 'var(--purple-bg)' },
};

function TypeBadge({ type }) {
  const cfg = typeConfig[type] || { color: 'var(--text-muted)', bg: 'var(--bg-subtle)' };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}`,
      borderRadius: '4px', padding: '2px 10px', fontSize: '11px', fontWeight: 'bold',
    }}>
      {type}
    </span>
  );
}

// ─── Status badge (for project list in panel) ─────────────────────────────────

const statusConfig = {
  'Planning':  { color: 'var(--info-text)', bg: 'var(--info-bg)' },
  'Confirmed': { color: 'var(--purple-text)', bg: 'var(--purple-bg)' },
  'Active':    { color: 'var(--success-text)', bg: 'var(--success-bg)' },
  'On Hold':   { color: 'var(--warning-text)', bg: 'var(--warning-bg)' },
  'Completed': { color: 'var(--text-muted)',    bg: 'var(--bg-subtle)' },
  'Canceled':  { color: 'var(--accent)', bg: 'var(--danger-bg)' },
};

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || { color: 'var(--text-muted)', bg: 'var(--bg-subtle)' };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}`,
      borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 'bold',
    }}>
      {status}
    </span>
  );
}

// ─── Sort icon ────────────────────────────────────────────────────────────────

function SortIcon({ colKey, sortKey, sortDir }) {
  if (sortKey !== colKey) return <span style={{ color: 'var(--border-strong)', marginLeft: '4px' }}>↕</span>;
  return <span style={{ color: 'var(--accent)', marginLeft: '4px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
}

// ─── Row Actions Menu ─────────────────────────────────────────────────────────

function RowActionsMenu({ onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ top: 0, left: 0 });
  const btnRef          = useRef(null);

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.right });
    }
    setOpen(p => !p);
  };

  const item = (label, icon, color, onClick) => (
    <div
      onClick={e => { e.stopPropagation(); onClick(); setOpen(false); }}
      style={{ padding: '9px 14px', cursor: 'pointer', borderRadius: '4px', color: color || 'var(--text-body)', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <span>{icon}</span>{label}
    </div>
  );

  return (
    <div style={{ display: 'inline-block' }} onClick={e => e.stopPropagation()}>
      <button
        ref={btnRef}
        onClick={handleOpen}
        style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-dim)', padding: '4px 8px', borderRadius: '6px', fontSize: '14px', cursor: 'pointer', lineHeight: 1 }}
        title="More actions">
        ⋮
      </button>
      {open && ReactDOM.createPortal(
        <>
          <div onClick={e => { e.stopPropagation(); setOpen(false); }} style={{ position: 'fixed', inset: 0, zIndex: 990 }} />
          <div style={{
            position: 'fixed', top: pos.top, left: pos.left, transform: 'translateX(-100%)',
            background: 'var(--bg-menu)', border: '1px solid var(--border-strong)', borderRadius: '8px',
            padding: '4px', zIndex: 1000, boxShadow: '0 8px 24px rgba(0,0,0,0.6)', minWidth: '160px',
          }}>
            {item('Edit', '✏️', 'var(--text-body)', onEdit)}
            <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }} />
            {item('Delete', '🗑', 'var(--accent)', onDelete)}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// ─── Delete Modal ─────────────────────────────────────────────────────────────

function DeleteModal({ company, onConfirm, onCancel }) {
  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: '12px', padding: '32px', maxWidth: '400px', width: '90%', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ fontSize: '32px', textAlign: 'center', marginBottom: '16px' }}>🗑</div>
        <h3 style={{ color: 'var(--text-primary)', textAlign: 'center', margin: '0 0 8px 0', fontSize: '18px' }}>Delete company?</h3>
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', fontSize: '13px', margin: '0 0 24px 0', lineHeight: '1.5' }}>
          Permanently delete <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>"{company.name}"</span>?<br />
          This action cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button onClick={onCancel}  style={{ background: 'transparent', border: '1px solid var(--text-disabled)', color: 'var(--text-secondary)', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
          <button onClick={onConfirm} style={{ background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>Delete</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Company Form (Add / Edit) ────────────────────────────────────────────────

function emptyContact() {
  return { id: Date.now() + Math.random(), name: '', phone: '', email: '', role: '' };
}

function CompanyForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(() => initial
    ? {
        type: 'Client',
        ...initial,
        contacts: (initial.contacts || []).length
          ? initial.contacts.map(c => ({ ...c }))
          : [emptyContact()],
      }
    : { name: '', country: '', city: '', type: 'Client', contacts: [emptyContact()], notes: '' }
  );

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const setContact = (idx, k, v) => setForm(p => {
    const contacts = p.contacts.map((c, i) => i === idx ? { ...c, [k]: v } : c);
    return { ...p, contacts };
  });

  const addContact    = () => setForm(p => ({ ...p, contacts: [...p.contacts, emptyContact()] }));
  const removeContact = idx => setForm(p => ({ ...p, contacts: p.contacts.filter((_, i) => i !== idx) }));

  const handleSubmit = e => {
    e.preventDefault();
    if (!form.name.trim()) return;
    // Не зберігати повністю порожні контакти
    const contacts = form.contacts.filter(c =>
      (c.name || '').trim() || (c.email || '').trim() || (c.phone || '').trim() || (c.role || '').trim()
    );
    onSave({ ...form, contacts, id: initial?.id || Date.now() });
  };

  const fi = { ...inputStyle, marginBottom: 0 };

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto', fontFamily: 'Arial, sans-serif' }}>

        {/* Header */}
        <div style={{ padding: '24px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '18px' }}>{initial ? 'Edit Company' : 'Add Company'}</h2>
          <button onClick={onCancel} style={{ background: 'transparent', border: 'none', color: 'var(--text-dim)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '0 28px 28px' }}>

          {/* Name */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Company Name *</label>
            <input style={fi} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. EventPro GmbH" required />
          </div>

          {/* Country + City */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Country</label>
              <input style={fi} value={form.country} onChange={e => set('country', e.target.value)} placeholder="Germany" />
            </div>
            <div>
              <label style={labelStyle}>City</label>
              <input style={fi} value={form.city} onChange={e => set('city', e.target.value)} placeholder="Berlin" />
            </div>
          </div>

          {/* Type */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Type</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {COMPANY_TYPES.map(t => (
                <button
                  key={t} type="button"
                  onClick={() => set('type', t)}
                  style={{
                    padding: '6px 18px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
                    border: `1px solid ${form.type === t ? typeConfig[t].color : 'var(--border-strong)'}`,
                    background: form.type === t ? typeConfig[t].bg : 'transparent',
                    color: form.type === t ? typeConfig[t].color : 'var(--text-dim)',
                    fontWeight: form.type === t ? 'bold' : 'normal',
                  }}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Contacts */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Contacts</label>
              <button type="button" onClick={addContact}
                style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '3px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                + Add
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {form.contacts.map((c, idx) => (
                <div key={c.id} style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <input style={fi} placeholder="Full name" value={c.name}  onChange={e => setContact(idx, 'name',  e.target.value)} />
                    <input style={fi} placeholder="Role"      value={c.role}  onChange={e => setContact(idx, 'role',  e.target.value)} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <input style={fi} placeholder="Phone" value={c.phone} onChange={e => setContact(idx, 'phone', e.target.value)} />
                    <input style={fi} placeholder="Email" value={c.email} onChange={e => setContact(idx, 'email', e.target.value)} />
                  </div>
                  {form.contacts.length > 1 && (
                    <button type="button" onClick={() => removeContact(idx)}
                      style={{ marginTop: '8px', background: 'transparent', border: 'none', color: 'var(--accent)', fontSize: '11px', cursor: 'pointer', padding: 0 }}>
                      Remove contact
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>Notes</label>
            <textarea
              style={{ ...fi, height: '72px', resize: 'vertical' }}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Internal notes..."
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onCancel}
              style={{ background: 'transparent', border: '1px solid var(--text-disabled)', color: 'var(--text-secondary)', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
              Cancel
            </button>
            <button type="submit"
              style={{ background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '10px 28px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
              {initial ? 'Save Changes' : 'Add Company'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ─── Side Panel ───────────────────────────────────────────────────────────────

function SidePanel({ company, projects, onClose, onEdit, onDelete }) {
  const companyProjects = projects.filter(p => p.company === company.name);
  const [dispCur, setDispCur] = useState('EUR');

  // Дохід/прибуток окремо по кожній валюті, в якій виставлені проєкти компанії
  const perCur = useMemo(() => {
    const m = {};
    companyProjects.forEach(p => {
      const c = curOf(p);
      if (!m[c]) m[c] = { income: 0, outcome: 0 };
      m[c].income  += p.income  || 0;
      m[c].outcome += p.outcome || 0;
    });
    return m;
  }, [companyProjects]);

  const curKeys  = Object.keys(perCur);
  const isMixed  = curKeys.length > 1;
  const soleCur  = curKeys[0] || 'EUR';

  // Загальна сума, конвертована (за приблизним курсом) в обрану адміном валюту
  const totalConverted = useMemo(() => {
    const target = CURRENCY_META[dispCur]?.rate || 1;
    return Object.entries(perCur).reduce((acc, [c, t]) => {
      const rate = CURRENCY_META[c]?.rate || 1;
      const k = target / rate; // c → EUR → dispCur
      acc.income  += t.income  * k;
      acc.outcome += t.outcome * k;
      return acc;
    }, { income: 0, outcome: 0 });
  }, [perCur, dispCur]);

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: '420px',
      background: 'var(--bg-panel)', borderLeft: '1px solid var(--border)',
      zIndex: 500, display: 'flex', flexDirection: 'column',
      fontFamily: 'Arial, sans-serif', overflowY: 'auto',
    }}>
      {/* Header */}
      <div style={{ padding: '24px 24px 20px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-panel)', zIndex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <TypeBadge type={company.type} />
            <h2 style={{ color: 'var(--text-primary)', margin: '10px 0 4px', fontSize: '20px', fontWeight: 'bold' }}>{company.name}</h2>
            <div style={{ color: 'var(--text-dim)', fontSize: '13px' }}>
              {[company.city, company.country].filter(Boolean).join(', ') || '—'}
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: 'var(--text-dim)', fontSize: '22px', cursor: 'pointer', lineHeight: 1, marginTop: '4px' }}>
            ×
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          <button onClick={onEdit}
            style={{ flex: 1, background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-body)', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
            ✏️ Edit
          </button>
          <button onClick={onDelete}
            style={{ background: 'transparent', border: '1px solid #7f1d1d', color: 'var(--accent)', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
            🗑
          </button>
        </div>
      </div>

      <div style={{ padding: '20px 24px', flex: 1 }}>

        {/* Finance summary */}
        <div style={{ display: 'grid', gridTemplateColumns: isMixed ? '1fr' : '1fr 1fr 1fr', gap: '10px', marginBottom: isMixed ? '12px' : '24px' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 12px', textAlign: isMixed ? 'left' : 'center', display: isMixed ? 'flex' : 'block', justifyContent: isMixed ? 'space-between' : undefined, alignItems: isMixed ? 'center' : undefined }}>
            <div style={{ color: 'var(--text-dim)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: isMixed ? 0 : '6px' }}>Projects</div>
            <div style={{ color: 'var(--info-text)', fontSize: '22px', fontWeight: 'bold' }}>{companyProjects.length}</div>
          </div>
          {!isMixed && (
            <>
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 12px', textAlign: 'center' }}>
                <div style={{ color: 'var(--text-dim)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Income</div>
                <div style={{ color: 'var(--success-text)', fontSize: '18px', fontWeight: 'bold' }}>{fmtMoney(perCur[soleCur]?.income || 0, soleCur)}</div>
              </div>
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 12px', textAlign: 'center' }}>
                <div style={{ color: 'var(--text-dim)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Profit</div>
                <div style={{ color: (perCur[soleCur]?.income || 0) - (perCur[soleCur]?.outcome || 0) >= 0 ? 'var(--success-text)' : 'var(--accent)', fontSize: '18px', fontWeight: 'bold' }}>
                  {fmtMoney((perCur[soleCur]?.income || 0) - (perCur[soleCur]?.outcome || 0), soleCur)}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Компанія має проєкти в кількох валютах — показуємо кожну окремо + конвертовану суму */}
        {isMixed && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
              {curKeys.map(c => (
                <div key={c} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{c}</span>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: 'var(--success-text)', fontSize: '13px' }}>{fmtMoney(perCur[c].income, c)}</div>
                    <div style={{ color: (perCur[c].income - perCur[c].outcome) >= 0 ? 'var(--success-text)' : 'var(--accent)', fontSize: '11px' }}>
                      profit: {fmtMoney(perCur[c].income - perCur[c].outcome, c)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ color: 'var(--text-primary)', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>Total (converted)</span>
                <select
                  value={dispCur}
                  onChange={e => setDispCur(e.target.value)}
                  style={{ background: 'var(--bg-input)', border: '1px solid var(--border-strong)', borderRadius: '4px', color: 'var(--text-body)', fontSize: '11px', padding: '3px 6px' }}>
                  {Object.keys(CURRENCY_META).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '20px' }}>
                <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Income: <span style={{ color: 'var(--success-text)', fontWeight: 'bold' }}>{fmtMoney(totalConverted.income, dispCur)}</span></span>
                <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Profit: <span style={{ color: (totalConverted.income - totalConverted.outcome) >= 0 ? 'var(--success-text)' : 'var(--accent)', fontWeight: 'bold' }}>{fmtMoney(totalConverted.income - totalConverted.outcome, dispCur)}</span></span>
              </div>
              <div style={{ color: 'var(--text-disabled)', fontSize: '10px', marginTop: '6px' }}>за приблизним курсом</div>
            </div>
          </div>
        )}

        {/* Contacts */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ color: 'var(--text-dim)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Contacts</div>
          {(company.contacts || []).length === 0 ? (
            <div style={{ color: 'var(--text-disabled)', fontSize: '13px' }}>No contacts</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {(company.contacts || []).map(c => (
                <div key={c.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '14px' }}>{c.name}</span>
                    {c.role && <span style={{ color: 'var(--text-dim)', fontSize: '11px', background: 'var(--border)', padding: '2px 8px', borderRadius: '4px' }}>{c.role}</span>}
                  </div>
                  {c.phone && <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginBottom: '3px' }}>📞 {c.phone}</div>}
                  {c.email && <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>✉️ {c.email}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Projects */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ color: 'var(--text-dim)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Projects</div>
          {companyProjects.length === 0 ? (
            <div style={{ color: 'var(--text-disabled)', fontSize: '13px' }}>No projects yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {companyProjects.map(p => (
                <div key={p.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 'bold', marginBottom: '4px' }}>{p.name}</div>
                    <StatusBadge status={p.status} />
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: 'var(--success-text)', fontSize: '12px' }}>{fmtMoney(p.income, curOf(p))}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
                      profit: {fmtMoney((p.income || 0) - (p.outcome || 0), curOf(p))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        {company.notes && (
          <div>
            <div style={{ color: 'var(--text-dim)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Notes</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: '1.6', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px' }}>
              {company.notes}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const SORT_KEYS = ['name', 'country', 'city', 'type', 'projects', 'income'];

export default function CompaniesPage({ onBack, companies, setCompanies, projects = [] }) {
  const [search, setSearch]       = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [sortKey, setSortKey]     = useState('name');
  const [sortDir, setSortDir]     = useState('asc');
  const [panelCompany, setPanelCompany] = useState(null);
  const [formData, setFormData]   = useState(null);   // null = closed, {} = new, {...} = edit
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Derived: projects count + income per company, розбитий по валютах проєктів
  const companyStats = useMemo(() => {
    const map = {};
    companies.forEach(c => {
      const ps = projects.filter(p => !p.archived && p.company === c.name);
      const byCur = {};
      ps.forEach(p => {
        const cur = curOf(p);
        byCur[cur] = (byCur[cur] || 0) + (p.income || 0);
      });
      // Еквівалент у EUR — лише для сортування колонки (не для відображення)
      const incomeEUR = Object.entries(byCur).reduce(
        (s, [cur, val]) => s + val / (CURRENCY_META[cur]?.rate || 1), 0
      );
      map[c.id] = { projects: ps.length, byCur, incomeEUR };
    });
    return map;
  }, [companies, projects]);

  const handleSort = key => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    let list = companies.filter(c => {
      const q = search.toLowerCase();
      if (q && !`${c.name} ${c.city} ${c.country}`.toLowerCase().includes(q)) return false;
      if (typeFilter !== 'All' && c.type !== typeFilter) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      let av, bv;
      if (sortKey === 'projects') { av = companyStats[a.id]?.projects ?? 0; bv = companyStats[b.id]?.projects ?? 0; }
      else if (sortKey === 'income') { av = companyStats[a.id]?.incomeEUR ?? 0; bv = companyStats[b.id]?.incomeEUR ?? 0; }
      else { av = (a[sortKey] || '').toLowerCase(); bv = (b[sortKey] || '').toLowerCase(); }
      if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return list;
  }, [companies, search, typeFilter, sortKey, sortDir, companyStats]);

  const handleSave = async data => {
    try {
      const isNew = !data.id || !companies.find(c => c.id === data.id);
      const res = isNew
        ? await api.companies.create(data)
        : await api.companies.update(data.id, data);
      const saved = res.company;
      setCompanies(prev => {
        const exists = prev.find(c => c.id === saved.id);
        return exists ? prev.map(c => c.id === saved.id ? saved : c) : [saved, ...prev];
      });
      if (panelCompany?.id === saved.id) setPanelCompany(saved);
    } catch (e) {
      console.warn('API save error, falling back to local:', e);
      const fallback = { ...data, id: data.id || Date.now() };
      setCompanies(prev => {
        const exists = prev.find(c => c.id === fallback.id);
        return exists ? prev.map(c => c.id === fallback.id ? fallback : c) : [fallback, ...prev];
      });
      if (panelCompany?.id === data.id) setPanelCompany(fallback);
    }
    setFormData(null);
  };

  const handleDelete = async () => {
    try { await api.companies.delete(deleteTarget.id); } catch (e) { console.warn('API delete error:', e); }
    setCompanies(prev => prev.filter(c => c.id !== deleteTarget.id));
    if (panelCompany?.id === deleteTarget.id) setPanelCompany(null);
    setDeleteTarget(null);
  };

  const th = key => ({
    ...thBase,
    color: sortKey === key ? 'var(--accent)' : 'var(--text-dim)',
  });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}>

      {/* Nav */}
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onBack}
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
            ← Back
          </button>
          <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '18px', letterSpacing: '2px' }}>COMPANIES</span>
          <span style={{ color: 'var(--text-dim)', fontSize: '13px' }}>{companies.length} total</span>
        </div>
        <button
          onClick={() => setFormData({})}
          style={{ background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '8px 18px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
          + Add Company
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: '28px 32px', marginRight: panelCompany ? '420px' : 0, transition: 'margin-right 0.2s' }}>

        {/* Filters bar */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            style={{ ...inputStyle, width: '260px' }}
            placeholder="🔍  Search by name, city, country..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div style={{ display: 'flex', gap: '6px' }}>
            {['All', ...COMPANY_TYPES].map(t => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                style={{
                  padding: '7px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
                  border: `1px solid ${typeFilter === t ? (typeConfig[t]?.color || 'var(--accent)') : 'var(--border-strong)'}`,
                  background: typeFilter === t ? (typeConfig[t]?.bg || 'var(--danger-bg)') : 'transparent',
                  color: typeFilter === t ? (typeConfig[t]?.color || 'var(--accent)') : 'var(--text-dim)',
                  fontWeight: typeFilter === t ? 'bold' : 'normal',
                }}>
                {t}
              </button>
            ))}
          </div>
          {(search || typeFilter !== 'All') && (
            <button
              onClick={() => { setSearch(''); setTypeFilter('All'); }}
              style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '7px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
              Clear
            </button>
          )}
          <span style={{ color: 'var(--text-disabled)', fontSize: '12px', marginLeft: 'auto' }}>{filtered.length} of {companies.length}</span>
        </div>

        {/* Table */}
        <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[
                  { key: 'name',     label: 'Company' },
                  { key: 'country',  label: 'Country' },
                  { key: 'city',     label: 'City' },
                  { key: 'type',     label: 'Type' },
                  { key: 'contacts', label: 'Contacts', noSort: true },
                  { key: 'projects', label: 'Projects' },
                  { key: 'income',   label: 'Total Income' },
                ].map(col => (
                  <th
                    key={col.key}
                    style={col.noSort ? { ...thBase, cursor: 'default' } : th(col.key)}
                    onClick={() => !col.noSort && handleSort(col.key)}>
                    {col.label}
                    {!col.noSort && <SortIcon colKey={col.key} sortKey={sortKey} sortDir={sortDir} />}
                  </th>
                ))}
                <th style={{ ...thBase, cursor: 'default' }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-disabled)', padding: '48px' }}>
                    No companies found
                  </td>
                </tr>
              ) : filtered.map(c => {
                const stats = companyStats[c.id] || { projects: 0, income: 0 };
                const contacts = c.contacts || [];
                const isActive = panelCompany?.id === c.id;
                return (
                  <tr
                    key={c.id}
                    onClick={() => setPanelCompany(isActive ? null : c)}
                    style={{ cursor: 'pointer', background: isActive ? 'var(--bg-hover)' : 'transparent', borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent' }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}>
                    <td style={tdStyle}>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '14px' }}>{c.name}</div>
                    </td>
                    <td style={tdStyle}>{c.country || <span style={{ color: 'var(--text-disabled)' }}>—</span>}</td>
                    <td style={tdStyle}>{c.city    || <span style={{ color: 'var(--text-disabled)' }}>—</span>}</td>
                    <td style={tdStyle}><TypeBadge type={c.type} /></td>
                    <td style={tdStyle}>
                      {contacts.length === 0
                        ? <span style={{ color: 'var(--text-disabled)' }}>—</span>
                        : <div>
                            <div style={{ color: 'var(--text-body)', fontSize: '13px', fontWeight: 'bold' }}>{contacts[0].name}</div>
                            {contacts[0].email && <div style={{ color: 'var(--text-dim)', fontSize: '11px' }}>{contacts[0].email}</div>}
                            {contacts.length > 1 && <div style={{ color: 'var(--text-disabled)', fontSize: '11px' }}>+{contacts.length - 1} more</div>}
                          </div>
                      }
                    </td>
                    <td style={tdStyle}>
                      <span style={{ color: 'var(--info-text)', fontWeight: 'bold' }}>{stats.projects}</span>
                    </td>
                    <td style={tdStyle}>
                      {(() => {
                        const cKeys = Object.keys(stats.byCur || {});
                        if (cKeys.length === 0) return <span style={{ color: 'var(--text-disabled)' }}>—</span>;
                        const text = cKeys.map(c => fmtMoney(stats.byCur[c], c)).join(' + ');
                        return (
                          <span style={{ color: 'var(--success-text)', fontWeight: 'bold' }} title={cKeys.length > 1 ? text : undefined}>
                            {text}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      <RowActionsMenu
                        onEdit={() => setFormData(c)}
                        onDelete={() => setDeleteTarget(c)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Side Panel */}
      {panelCompany && (
        <SidePanel
          company={panelCompany}
          projects={projects}
          onClose={() => setPanelCompany(null)}
          onEdit={() => setFormData(panelCompany)}
          onDelete={() => setDeleteTarget(panelCompany)}
        />
      )}

      {/* Form Modal */}
      {formData !== null && (
        <CompanyForm
          initial={Object.keys(formData).length > 0 ? formData : null}
          onSave={handleSave}
          onCancel={() => setFormData(null)}
        />
      )}

      {/* Delete Modal */}
      {deleteTarget && (
        <DeleteModal
          company={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
